import * as React from "react";
import { useHistory, useParams } from "react-router";
import { camelCase } from "lodash";

import { PageWrapper } from "@/components/PageWrapper";
import { Fans } from "@/containers/Coach/Reputation/Fans";
import { ReviewInbox } from "./components/ReviewInbox";
import { URLS, useSocialCoachSession } from "@/utils";
import { FormLinksTable } from "./components/FormLinksTable";
import { Tabs } from "@/components/ui/tabs";
import { History, Inbox, Settings, Share2, Users } from "lucide-react";
import { Button } from "@/components/ui";
import { ShareFormLinkDialog } from "@/components/ShareFormLinkDialog";
import { useState } from "react";
import { cn } from "@/lib/utils";
import { SocialNetworkProfiles } from "@/components/SocialNetworkProfiles";
import { ReputationHeader, ReputationTab } from "@/containers/Coach/Reputation/components/ReputationHeader";

const PlayerReputationFC: React.FC = () => {
  const history = useHistory();
  const { playerId } = useSocialCoachSession();
  const [showReviewLinkDialog, setShowReviewLinkDialog] = useState(false);

  const { tab } = useParams<{ tab: string }>();
  const [selectedTabKey, setSelectedTabKey] = useState<string>(tab === "reviews" ? "inbox" : tab || "inbox");

  React.useEffect(() => {
    if (tab) {
      if (tab === "reviews") {
        setSelectedTabKey("inbox");
      } else {
        setSelectedTabKey(tab);
      }
    }
  }, [tab]);

  React.useEffect(() => {
    history.replace(URLS.player.reputation.tab.replace(":tab?", camelCase(selectedTabKey)));
  }, [selectedTabKey]);

  const secondaryTabs: ReputationTab[] = [
    { key: "fans", title: "Fans", icon: Users },
    { key: "sources", title: "Review Sources", icon: Settings },
    { key: "forms", title: "Sent Links", icon: History },
  ];

  return (
    <PageWrapper hideSeparator fitViewport={true}>
      <ReputationHeader
        selectedTabKey={selectedTabKey}
        onTabChange={setSelectedTabKey}
        mainTabKey="inbox"
        mainTabTitle="Review Inbox"
        mainTabIcon={Inbox}
        backToMainTitle="Back to Review Inbox"
        tabs={secondaryTabs}
        rightActions={
          <Button
            variant="default"
            onClick={() => {
              setShowReviewLinkDialog(true);
            }}
          >
            <Share2 className="size-4 shrink-0 text-current mr-1" />
            Share Review Link
          </Button>
        }
      />

      <Tabs
        value={selectedTabKey}
        onValueChange={(val: any) => setSelectedTabKey(val)}
        className="flex flex-col flex-1 overflow-hidden min-h-0 w-full"
      >
        <Tabs.Content
          value="inbox"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "inbox" ? "overflow-hidden" : "hidden")}
        >
          {selectedTabKey === "inbox" && (
            <ReviewInbox playerId={playerId} onShareReviewLink={() => setShowReviewLinkDialog(true)} />
          )}
        </Tabs.Content>

        <Tabs.Content
          value="fans"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "fans" ? "p-4 overflow-hidden" : "hidden")}
        >
          {selectedTabKey === "fans" && <Fans isPlayerContext={true} playerId={playerId} />}
        </Tabs.Content>

        <Tabs.Content
          value="sources"
          className={cn(
            "flex flex-col flex-1 min-h-0",
            selectedTabKey === "sources" ? "p-4 overflow-y-auto" : "hidden"
          )}
        >
          {selectedTabKey === "sources" && <SocialNetworkProfiles networkFilter={["FB", "GL", "ZL"]} />}
        </Tabs.Content>

        <Tabs.Content
          value="forms"
          className={cn("flex flex-col flex-1 min-h-0", selectedTabKey === "forms" ? "p-4 overflow-y-auto" : "hidden")}
        >
          {selectedTabKey === "forms" && <FormLinksTable />}
        </Tabs.Content>
      </Tabs>

      <ShareFormLinkDialog open={showReviewLinkDialog} onOpenChange={setShowReviewLinkDialog} playerId={playerId!} />
    </PageWrapper>
  );
};

export const PlayerReputation = PlayerReputationFC;
